/**
 * The download progress toasts (VE-26426).
 *
 * Renders nothing where it is mounted — it drives react-toastify from the
 * downloads duck, building each toast's body as it goes. Mounted once, beside
 * the tray, so a download started anywhere in the app reports progress the same
 * way; before this, only the folder view's two bulk flows showed any progress at
 * all, and they showed a raw byte count that jumped backwards whenever the three
 * concurrent transfers interleaved.
 *
 * Each body is exactly two lines that clip rather than wrap. As one sentence it
 * re-wrapped between one and two lines while the numbers in it changed width, so
 * the toast changed height every few hundred milliseconds — the same restlessness
 * this ticket exists to remove, arriving through its replacement.
 *
 * One toast per batch, not one overall. Two downloads can run at once, and a
 * single toast would have to either merge them into a figure that describes
 * neither, or let the second overwrite the first. They stack instead — toastify
 * lays them out in creation order — and each closes when its own batch finishes,
 * rather than all of them waiting on the slowest.
 *
 * The counts on the first line are the whole batch. The second names one file —
 * the lowest-indexed still going, so it changes only when that file finishes
 * rather than flickering between three — and says how many others are moving
 * with it, because a single name standing silently for three reads as though the
 * figures above were about that one file.
 *
 * Each keeps its close button: the user can dismiss one and keep the header
 * tray, and a dismissal sticks rather than being undone by the next progress
 * event.
 */
import { CSSProperties, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Id, toast } from 'react-toastify';

import { I18nTranslate } from '@i18n';

import {
  DownloadBatchSummary,
  selectDownloadBatchSummaries,
} from '@redact-modules/downloads';

import { toastOptions } from '../CaseMedia/CaseFolders/ToastOptions';
import { formatDownloadProgress, formatDownloadSize } from './formatSize';

/** Both lines clip rather than wrap — see `messageFor`. */
const LINE: CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/**
 * Wide enough for the longest line this can produce — "Downloading 10/10 —
 * 999.9 / 999.9 MB" — inside toastify's 320px shell. The counts and the numbers
 * both change width as a batch runs, so the line is sized for its worst case
 * rather than its current one.
 */
const TOAST_BODY: CSSProperties = { maxWidth: 270, minWidth: 0 };

const FILENAME_LINE: CSSProperties = {
  display: 'flex',
  fontSize: '0.8125rem',
  opacity: 0.9,
};

/** Takes the truncation, so the count beside it is never the part cut off. */
const FILENAME_TEXT: CSSProperties = { ...LINE, minWidth: 0 };

/**
 * Never shrinks. Appending "+2 more" to the name as one string would let the
 * ellipsis eat the very thing it was added to say.
 */
const FILENAME_SUFFIX: CSSProperties = { flexShrink: 0, marginLeft: 4 };

const DownloadProgressToast = () => {
  const intl = I18nTranslate.Intl();
  const summaries = useSelector(selectDownloadBatchSummaries);
  /** Live toast per batch id, so each batch updates and closes its own. */
  const toastIds = useRef(new Map<string, Id>());
  const dismissedByUser = useRef(new Set<string>());
  /** Batches this component closed itself, so `onClose` can tell them apart. */
  const dismissedProgrammatically = useRef(new Set<string>());

  useEffect(() => {
    const progressLine = (summary: DownloadBatchSummary) =>
      summary.totalBytes === undefined
        ? intl.formatMessage(
            {
              id: 'downloadProgressNoSize',
              defaultMessage: 'Downloading {done}/{total} — {loaded}',
            },
            {
              done: summary.done,
              total: summary.total,
              loaded: formatDownloadSize(summary.loadedBytes),
            }
          )
        : intl.formatMessage(
            {
              id: 'downloadProgress',
              defaultMessage: 'Downloading {done}/{total} — {progress}',
            },
            {
              done: summary.done,
              total: summary.total,
              progress: formatDownloadProgress(
                summary.loadedBytes,
                summary.totalBytes
              ),
            }
          );

    /**
     * Always exactly two lines, neither of which wraps: progress on top, the
     * file being written underneath. A single sentence re-wrapped between one
     * and two lines as the numbers changed width, so the toast changed height
     * every few hundred milliseconds. Fixing the line count fixes the height,
     * and the filename is truncated rather than allowed to push it back out.
     */
    const messageFor = (summary: DownloadBatchSummary) => (
      <div style={TOAST_BODY} data-testid="download-progress-toast">
        <div style={LINE}>{progressLine(summary)}</div>
        <div style={FILENAME_LINE}>
          <span style={FILENAME_TEXT} title={summary.currentName}>
            {summary.currentName}
          </span>
          {summary.activeCount > 1 && (
            <span style={FILENAME_SUFFIX}>
              {intl.formatMessage(
                {
                  id: 'downloadProgressMoreFiles',
                  defaultMessage: '+{count} more',
                },
                { count: summary.activeCount - 1 }
              )}
            </span>
          )}
        </div>
      </div>
    );

    const live = new Set(summaries.map((summary) => summary.id));

    // A batch that finished — or was cleared out from under us — takes its toast
    // with it. The "Download All completed." snackbar reports the outcome, and
    // the finished batch stays in the tray to be looked at.
    toastIds.current.forEach((toastId, batchId) => {
      const summary = summaries.find((candidate) => candidate.id === batchId);
      if (!summary || !summary.isActive) {
        // react-toastify fires `onClose` for any removal, ours included, and
        // only after the exit animation. Without this the close below would
        // come back a few hundred milliseconds later claiming the user had
        // dismissed the batch.
        dismissedProgrammatically.current.add(batchId);
        toast.dismiss(toastId);
        toastIds.current.delete(batchId);
      }
    });
    dismissedByUser.current.forEach((batchId) => {
      if (!live.has(batchId)) {
        dismissedByUser.current.delete(batchId);
      }
    });

    summaries.forEach((summary) => {
      if (!summary.isActive || dismissedByUser.current.has(summary.id)) {
        return;
      }
      const options = {
        ...toastOptions,
        onClose: () => {
          toastIds.current.delete(summary.id);
          if (dismissedProgrammatically.current.delete(summary.id)) {
            return;
          }
          dismissedByUser.current.add(summary.id);
        },
      };
      const existing = toastIds.current.get(summary.id);
      if (existing === undefined) {
        toastIds.current.set(summary.id, toast(messageFor(summary), options));
      } else {
        toast.update(existing, { ...options, render: messageFor(summary) });
      }
    });
  }, [summaries, intl]);

  // Dismiss on unmount so no toast outlives the app bar that owns it.
  useEffect(() => {
    const live = toastIds.current;
    return () => {
      live.forEach((toastId) => toast.dismiss(toastId));
      live.clear();
    };
  }, []);

  return null;
};

export default DownloadProgressToast;
